import * as Yup from 'yup';

/**
 * What a password must contain — the only place it is defined.
 *
 * The signup form, the reset form and the on-screen checklist each used to carry their
 * own copy, and the copies drifted: the checklist ticked off "special character" for
 * `_`, `-` or `.` while the reset form only accepted `@$!%*?&#`. The form then refused
 * to submit with every requirement showing as met. Validation and checklist both read
 * from this list now, so they cannot disagree.
 */
const SPECIAL_CHARACTER = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export const PASSWORD_REQUIREMENTS = [
  {
    id: 'length',
    label: 'At least 8 characters',
    error: 'Password must be at least 8 characters long',
    met: (password: string) => password.length >= 8,
  },
  {
    id: 'lowercase',
    label: 'At least 1 lowercase letter',
    error: 'Password must contain at least one lowercase letter',
    met: (password: string) => /[a-z]/.test(password),
  },
  {
    id: 'uppercase',
    label: 'At least 1 uppercase letter',
    error: 'Password must contain at least one uppercase letter',
    met: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: 'number',
    label: 'At least 1 number',
    error: 'Password must contain at least one number',
    met: (password: string) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'At least 1 special character',
    error: 'Password must contain at least one special character',
    met: (password: string) => SPECIAL_CHARACTER.test(password),
  },
] as const;

/** The Yup rule for a new password: every requirement above, in the order listed. */
export const passwordSchema = () =>
  PASSWORD_REQUIREMENTS.reduce(
    (schema, { id, error, met }) =>
      schema.test(id, error, (value) => met(value ?? '')),
    Yup.string()
  ).required('Password is required');
